// ─────────────────────────────────────────────────────────────────────────────
// RexyGraphStructureTree — the live structural index.
//
// WHY THIS EXISTS. The galaxy shows WHERE things are; it is a poor way to learn
// WHAT exists. The owner had to fly around hunting particles and still could not
// tell a domain from an agent. This panel is the index: every real domain, every
// registered agent beneath it, and every proven structural child beneath that.
//
// IT READS THE STORE, NOT A LIST OF ITS OWN. Every row comes from the loaded
// graph — the same nodes the renderer draws, from the same live DTO. There is no
// second roster here to drift: if a domain is in this tree it is because a node
// exists, and if an agent is missing from this tree it is missing from the graph.
//
// EXACT TYPES, NEVER "RECORDS". A generic bucket label is actively misleading in
// G2.1: there are zero record nodes, so calling anything "Records" would name a
// thing that does not exist. Every group heading is the real structural type.
//
// MATURITY IS THREE-VALUED AND THE DISTINCTION IS LOAD-BEARING. `FOUND` is proven
// in the current OS. `NOT YET IMPLEMENTED` is an expected architectural area this
// agent has not been built out to. `UNKNOWN` is "may exist, no authoritative
// source proven". Collapsing these into "none" would turn Rexy's implementation
// immaturity into a permanent architectural claim, which it is not.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { RexyGraphNodeRecord } from './graphTypes';
import { RexyGraphStore } from './graphStore';
import { resolveDomainColour } from './graphPalette';
import { dockPanelTitleStyle } from './graphDock';

/** The owner-facing name for a structural kind. Never a backend token. */
export function structuralTypeLabel(node: RexyGraphNodeRecord): string {
  const meta = (node.metadata || {}) as { [k: string]: unknown };
  const sub = String(meta.subKind || '');

  // CANONICAL KINDS FIRST (G2.4). The backend now names each ownership class
  // outright — a primary agent is not a team member, and domain memory is not
  // agent memory — so the label is read off the kind instead of being inferred
  // from a subKind. The legacy branches below still answer for the synthetic
  // lab, which speaks the older dialect and is not being migrated.
  switch (node.kind) {
    case 'os-root': return 'REXY OS';
    case 'domain': return 'DOMAIN';
    case 'primary-agent': return 'PRIMARY AGENT';
    case 'team': return 'TEAM';
    case 'team-member': return 'TEAM MEMBER';
    case 'os-memory': return 'OS MEMORY';
    case 'domain-memory': return 'DOMAIN MEMORY';
    case 'agent-memory': return 'AGENT MEMORY';
    case 'team-memory': return 'TEAM MEMORY';
    case 'memory-file': return 'FILE';
    default: break;
  }

  if (node.kind === 'agent') return 'AGENT';
  if (node.kind === 'memory-system') {
    if (sub === 'domain-hive') return 'DOMAIN MEMORY';
    if (sub === 'agent-space' || sub === 'agent-store') return 'AGENT MEMORY';
    if (sub === 'os-master') return 'OS MEMORY';
    return 'MEMORY SYSTEM';
  }
  if (node.kind === 'database') return 'DATABASE';
  if (node.kind === 'workflow') return 'WORKFLOW';
  if (node.kind === 'model') return 'MODEL';
  if (node.kind === 'service') return 'SERVICE';
  if (node.kind === 'surface') return 'SURFACE';
  if (node.kind === 'city-hall') return 'REXY OS';
  return node.kind.toUpperCase();
}

/**
 * The owner-facing ownership line for a node — the second line of a hover
 * feather, a search result and the Inspector header, written once so all three
 * say the same thing.
 *
 * Built from canonical metadata only. `FORGE` reads `TEAM MEMBER · CODE · BUILD
 * TEAM` because the DTO proved the team, never because a name was matched.
 */
export function structuralContext(
  node: RexyGraphNodeRecord,
  lookup: (id: string) => RexyGraphNodeRecord | undefined,
): string {
  const meta = (node.metadata || {}) as { [k: string]: unknown };
  const parts: string[] = [structuralTypeLabel(node)];

  const domainKey = meta.domainKey ? String(meta.domainKey) : '';
  if (domainKey) parts.push(domainKey);

  const teamId = meta.teamId ? String(meta.teamId) : '';
  if (teamId) {
    const team = lookup(teamId);
    if (team) parts.push(team.label.toUpperCase());
  } else if (!domainKey && node.ownerId) {
    // Memory, workflows and databases name their OWNER instead of a domain:
    // "AGENT MEMORY · MACK" is the fact worth showing.
    const owner = lookup(node.ownerId);
    if (owner) parts.push(owner.label.toUpperCase());
  }
  return parts.join(' · ');
}

/** Group heading for a child, in the owner's vocabulary. */
function groupFor(node: RexyGraphNodeRecord): string {
  const t = structuralTypeLabel(node);
  // PRESENTATION HEADINGS ONLY. These group rows are UI, not topology — no
  // graph node is created for "PRIMARY AGENTS", and the tree's parentage still
  // comes entirely from canonical relations.
  if (t === 'PRIMARY AGENT') return 'PRIMARY AGENTS';
  if (t === 'TEAM') return 'TEAMS';
  if (t === 'TEAM MEMBER') return 'TEAM MEMBERS';
  if (t === 'DOMAIN MEMORY') return 'DOMAIN MEMORY';
  if (t === 'AGENT MEMORY') return 'AGENT MEMORY';
  if (t === 'TEAM MEMORY') return 'TEAM MEMORY';
  if (t === 'OS MEMORY') return 'OS MEMORY';
  if (t === 'FILE') return 'FILES';
  if (t === 'DATABASE') return 'DATABASES';
  if (t === 'WORKFLOW') return 'WORKFLOWS';
  if (t === 'MODEL') return 'MODELS';
  if (t === 'SERVICE') return 'SERVICES';
  if (t === 'SURFACE') return 'SURFACES';
  return t;
}

// Order puts the OS's INHABITANTS first — agents, then teams — because that is
// what the owner scans a domain for. Memory follows, then the secondary systems,
// then surfaces, which are the least structural thing a domain owns.
const GROUP_ORDER = ['PRIMARY AGENTS', 'TEAMS', 'TEAM MEMBERS',
                     'OS MEMORY', 'DOMAIN MEMORY', 'AGENT MEMORY', 'TEAM MEMORY',
                     'FILES', 'DATABASES', 'MODELS',
                     'WORKFLOWS', 'SERVICES', 'SURFACES'];

export interface StructureTreeProps {
  store: RexyGraphStore;
  rootId: string;
  live: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onFocusNode: (id: string) => void;
  onOpen?: (node: RexyGraphNodeRecord) => void;
}

export function RexyGraphStructureTree({
  store, rootId, live, selectedId, onSelect, onFocusNode, onOpen,
}: StructureTreeProps): React.ReactElement {
  const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  const selectedRowRef = useRef<HTMLDivElement | null>(null);

  const toggle = (id: string) =>
    setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // TREE FOLLOWS GRAPH. Selecting a particle in the galaxy reveals its path here
  // — the two views must never disagree about where a thing lives. Expansion
  // only; the scroll happens on persistent selection, not on hover, so the tree
  // never thrashes under a moving pointer.
  useEffect(() => {
    if (!selectedId) return;
    const path: string[] = [];
    let cursor = store.getNode(selectedId);
    while (cursor && cursor.parentId) {
      path.push(cursor.parentId);
      cursor = store.getNode(cursor.parentId);
    }
    if (path.length) {
      setExpanded((e) => {
        const next = { ...e };
        path.forEach((id) => { next[id] = true; });
        return next;
      });
    }
  }, [selectedId, store]);

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedId]);

  const domains = useMemo(
    () => store.childrenOf(rootId)
      .map((id) => store.getNode(id))
      .filter((n): n is RexyGraphNodeRecord => !!n && n.kind === 'domain')
      .sort((a, b) => a.label.localeCompare(b.label)),
    [store, rootId],
  );

  // Root-owned components — MASTER memory lives here and nowhere else. It belongs
  // to no domain, so showing it under HOMEPAGE would state a parenthood the
  // runtime does not declare.
  const rootComponents = useMemo(
    () => store.childrenOf(rootId)
      .map((id) => store.getNode(id))
      .filter((n): n is RexyGraphNodeRecord => !!n && n.kind !== 'domain' && n.kind !== 'agent'),
    [store, rootId],
  );

  if (!live) {
    return (
      <div>
        <div style={dockPanelTitleStyle}>structure</div>
        <div style={{ color: '#4e6076', lineHeight: 1.5 }}>
          Synthetic lab. The structural index describes real Rexy OS only —
          open <code style={codeStyle}>?surface=graphlab&amp;source=live</code> in an
          authenticated session.
        </div>
      </div>
    );
  }

  const rootNode = store.getNode(rootId);

  return (
    <div>
      <div style={dockPanelTitleStyle}>structure</div>

      <Row
        depth={0}
        label={rootNode ? rootNode.label : 'REXY OS'}
        type="REXY OS"
        colour="#F5F7FA"
        selected={selectedId === rootId}
        rowRef={selectedId === rootId ? selectedRowRef : undefined}
        onSelect={() => onSelect(rootId)}
        onFocus={() => onFocusNode(rootId)}
      />

      {rootComponents.map((c) => (
        <React.Fragment key={c.id}>
          <GroupHeading depth={1} text={groupFor(c)} />
          <Row
            depth={2}
            label={c.label}
            type={structuralTypeLabel(c)}
            colour="#F5F7FA"
            selected={selectedId === c.id}
            rowRef={selectedId === c.id ? selectedRowRef : undefined}
            onSelect={() => onSelect(c.id)}
            onFocus={() => onFocusNode(c.id)}
          />
        </React.Fragment>
      ))}

      <GroupHeading depth={1} text={'DOMAINS · ' + domains.length} />

      {domains.map((d) => {
        const colour = resolveDomainColour(d.id, d.colour).hex;
        const kids = store.childrenOf(d.id).map((id) => store.getNode(id))
          .filter((n): n is RexyGraphNodeRecord => !!n);
        const agents = kids.filter((n) => n.kind === 'agent')
          .sort((a, b) => a.label.localeCompare(b.label));
        const domainComponents = kids.filter((n) => n.kind !== 'agent');
        const open = !!expanded[d.id];

        return (
          <div key={d.id}>
            <Row
              depth={1}
              label={d.label}
              type="DOMAIN"
              colour={colour}
              selected={selectedId === d.id}
              rowRef={selectedId === d.id ? selectedRowRef : undefined}
              expandable
              expanded={open}
              onToggle={() => toggle(d.id)}
              onSelect={() => onSelect(d.id)}
              onFocus={() => onFocusNode(d.id)}
              onOpen={d.openTarget && onOpen ? () => onOpen(d) : undefined}
            />

            {open && (
              <>
                {/* DOMAIN MEMORY IS LISTED SEPARATELY FROM AGENT MEMORY, above the
                    agents, because the whole point of the two-layer architecture is
                    that the hive is not one of the agents' possessions. */}
                {groupChildren(domainComponents).map(({ group, items }) => (
                  <React.Fragment key={group}>
                    <GroupHeading depth={2} text={group} />
                    {items.map((c) => (
                      <Row
                        key={c.id}
                        depth={3}
                        label={c.label}
                        type={structuralTypeLabel(c)}
                        colour={colour}
                        selected={selectedId === c.id}
                        rowRef={selectedId === c.id ? selectedRowRef : undefined}
                        onSelect={() => onSelect(c.id)}
                        onFocus={() => onFocusNode(c.id)}
                      />
                    ))}
                  </React.Fragment>
                ))}

                <GroupHeading depth={2} text={'AGENTS · ' + agents.length} />
                {agents.length === 0 && (
                  <Note depth={3} text="No registered agents — pipeline domain" />
                )}

                {agents.map((a) => {
                  const aKids = store.childrenOf(a.id).map((id) => store.getNode(id))
                    .filter((n): n is RexyGraphNodeRecord => !!n);
                  const aOpen = !!expanded[a.id];
                  return (
                    <div key={a.id}>
                      <Row
                        depth={3}
                        label={a.label}
                        type="AGENT"
                        colour={colour}
                        agent
                        selected={selectedId === a.id}
                        rowRef={selectedId === a.id ? selectedRowRef : undefined}
                        expandable
                        expanded={aOpen}
                        onToggle={() => toggle(a.id)}
                        onSelect={() => onSelect(a.id)}
                        onFocus={() => onFocusNode(a.id)}
                        onOpen={a.openTarget && onOpen ? () => onOpen(a) : undefined}
                      />
                      {aOpen && (
                        aKids.length === 0
                          ? <Note depth={4} text="No further structure implemented yet" />
                          : groupChildren(aKids).map(({ group, items }) => (
                            <React.Fragment key={group}>
                              <GroupHeading depth={4} text={group} />
                              {items.map((c) => (
                                <Row
                                  key={c.id}
                                  depth={5}
                                  label={c.label}
                                  type={structuralTypeLabel(c)}
                                  colour={colour}
                                  selected={selectedId === c.id}
                                  rowRef={selectedId === c.id ? selectedRowRef : undefined}
                                  onSelect={() => onSelect(c.id)}
                                  onFocus={() => onFocusNode(c.id)}
                                />
                              ))}
                            </React.Fragment>
                          ))
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function groupChildren(nodes: RexyGraphNodeRecord[]): { group: string; items: RexyGraphNodeRecord[] }[] {
  const buckets = new Map<string, RexyGraphNodeRecord[]>();
  nodes.forEach((n) => {
    const g = groupFor(n);
    if (!buckets.has(g)) buckets.set(g, []);
    (buckets.get(g) as RexyGraphNodeRecord[]).push(n);
  });
  const ordered: { group: string; items: RexyGraphNodeRecord[] }[] = [];
  GROUP_ORDER.forEach((g) => {
    const items = buckets.get(g);
    if (items) { ordered.push({ group: g, items: items.slice().sort((a, b) => a.label.localeCompare(b.label)) }); buckets.delete(g); }
  });
  buckets.forEach((items, g) => ordered.push({ group: g, items }));
  return ordered;
}

// ── rows ─────────────────────────────────────────────────────────────────────

const codeStyle: React.CSSProperties = { color: '#8fa6bd', background: '#12202e', padding: '1px 4px', borderRadius: 3 };

function GroupHeading({ depth, text }: { depth: number; text: string }): React.ReactElement {
  return (
    <div style={{
      paddingLeft: 8 + depth * 11, marginTop: 6, marginBottom: 2,
      color: '#4e6076', fontSize: 9.5, letterSpacing: 0.7, textTransform: 'uppercase',
    }}>
      {text}
    </div>
  );
}

function Note({ depth, text }: { depth: number; text: string }): React.ReactElement {
  return (
    <div style={{ paddingLeft: 8 + depth * 11, color: '#3d4c5e', fontSize: 10.5, fontStyle: 'italic', padding: '2px 0' }}>
      {text}
    </div>
  );
}

interface RowProps {
  depth: number;
  label: string;
  type: string;
  colour: string;
  agent?: boolean;
  selected: boolean;
  expandable?: boolean;
  expanded?: boolean;
  rowRef?: React.RefObject<HTMLDivElement | null>;
  onToggle?: () => void;
  onSelect: () => void;
  onFocus: () => void;
  onOpen?: () => void;
}

function Row({
  depth, label, type, colour, agent, selected, expandable, expanded,
  rowRef, onToggle, onSelect, onFocus, onOpen,
}: RowProps): React.ReactElement {
  return (
    <div
      ref={rowRef as React.RefObject<HTMLDivElement>}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        paddingLeft: 4 + depth * 11, paddingRight: 4,
        paddingTop: 2, paddingBottom: 2, borderRadius: 3,
        background: selected ? 'rgba(90,140,200,0.16)' : 'transparent',
      }}
    >
      {expandable ? (
        <button type="button" onClick={onToggle} aria-label={expanded ? 'Collapse ' + label : 'Expand ' + label}
          style={{ ...bareButton, width: 11, color: '#4e6076' }}>
          {expanded ? '▾' : '▸'}
        </button>
      ) : <span style={{ width: 11 }} />}

      {/* The dot carries the domain colour; an agent gets a white-hot centre, the
          same language the galaxy uses, so the tree and the particles read alike. */}
      <span style={{
        width: agent ? 7 : 8, height: agent ? 7 : 8, borderRadius: 5, flex: 'none',
        background: agent ? '#ffffff' : colour,
        boxShadow: agent ? '0 0 0 2px ' + colour : 'none',
      }} />

      <button type="button" onClick={onSelect} onDoubleClick={onFocus}
        title={label + ' — ' + type}
        style={{ ...bareButton, flex: 1, textAlign: 'left', color: '#cfe0f0', fontSize: 11.5 }}>
        {label}
        <span style={{ color: '#4e6076', marginLeft: 6, fontSize: 9.5 }}>{type}</span>
      </button>

      <button type="button" onClick={onFocus} title={'Focus ' + label}
        style={{ ...bareButton, color: '#4e6076', fontSize: 9.5 }}>
        focus
      </button>

      {/* OPEN APPEARS ONLY WHERE A REAL SURFACE EXISTS. No route, no button — the
          absence is the honest answer, not a disabled control implying a page. */}
      {onOpen && (
        <button type="button" onClick={onOpen} title={'Open ' + label}
          style={{ ...bareButton, color: '#5f8fc4', fontSize: 9.5 }}>
          open
        </button>
      )}
    </div>
  );
}

const bareButton: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, margin: 0,
  cursor: 'pointer', font: 'inherit',
};
